/** Short name; value is `sessionSampleRate,sessionReplaySampleRate` (e.g. `100,50`). */
export const DATADOG_SESSION_SAMPLE_RATE_COOKIE = "dd_sr";

const DEFAULT_SESSION_SAMPLE_RATE = 100;
const DEFAULT_SESSION_REPLAY_SAMPLE_RATE = 0;
const COOKIE_MAX_AGE_SECONDS = 60 * 60;

const clampSampleRate = (rate) => {
  const numericRate = Number(rate);
  if (!Number.isFinite(numericRate)) {
    return DEFAULT_SESSION_SAMPLE_RATE;
  }
  return Math.min(100, Math.max(0, numericRate));
};

const getEnvSessionSampleRate = () =>
  process.env.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE ||
  process.env.REACT_APP_DATADOG_SESSION_SAMPLE_RATE;

const getEnvSessionReplaySampleRate = () =>
  process.env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE ||
  process.env.REACT_APP_DATADOG_SESSION_REPLAY_SAMPLE_RATE;

const getDefaultDatadogSampleRates = () => ({
  sessionSampleRate: clampSampleRate(getEnvSessionSampleRate() ?? DEFAULT_SESSION_SAMPLE_RATE),
  sessionReplaySampleRate: clampSampleRate(
    getEnvSessionReplaySampleRate() ?? DEFAULT_SESSION_REPLAY_SAMPLE_RATE,
  ),
});

/**
 * Resolve LaunchDarkly flag `{ sessionSampleRate, sessionSampleReplayRate }` with env fallbacks.
 * Plain numbers are treated as sessionSampleRate only (replay uses env/default).
 */
export const resolveDatadogSessionSampleRates = (flagValue) => {
  const defaults = getDefaultDatadogSampleRates();

  if (flagValue === null || flagValue === undefined || flagValue === "") {
    return defaults;
  }

  if (typeof flagValue === "object" && !Array.isArray(flagValue)) {
    return {
      sessionSampleRate: clampSampleRate(flagValue.sessionSampleRate ?? defaults.sessionSampleRate),
      sessionReplaySampleRate: clampSampleRate(
        flagValue.sessionSampleReplayRate ??
          flagValue.sessionReplaySampleRate ??
          defaults.sessionReplaySampleRate,
      ),
    };
  }

  const parsedSessionRate = Number(flagValue);
  if (Number.isFinite(parsedSessionRate)) {
    return {
      ...defaults,
      sessionSampleRate: clampSampleRate(parsedSessionRate),
    };
  }

  return defaults;
};

export const formatDatadogSampleRatesCookieValue = ({
  sessionSampleRate,
  sessionReplaySampleRate,
}) => `${clampSampleRate(sessionSampleRate)},${clampSampleRate(sessionReplaySampleRate)}`;

/**
 * Parse `dd_sr=100,50` (or URL-encoded `100%2C50`) from a raw cookie value segment.
 */
export const parseDatadogSampleRatesCookieValue = (cookieValue) => {
  if (!cookieValue) {
    return null;
  }

  let decoded = cookieValue.trim();
  try {
    decoded = decodeURIComponent(decoded);
  } catch {
    /* malformed % sequence — parse raw value */
  }

  const [sessionPart, replayPart] = decoded.split(",").map((part) => part.trim());
  const sessionSampleRate = Number(sessionPart);
  if (!Number.isFinite(sessionSampleRate)) {
    return null;
  }

  const replayParsed = Number(replayPart);
  return {
    sessionSampleRate: clampSampleRate(sessionSampleRate),
    sessionReplaySampleRate: Number.isFinite(replayParsed)
      ? clampSampleRate(replayParsed)
      : DEFAULT_SESSION_REPLAY_SAMPLE_RATE,
  };
};

export const readDatadogSessionSampleRateCookie = () => {
  if (typeof document === "undefined") {
    return null;
  }

  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${DATADOG_SESSION_SAMPLE_RATE_COOKIE}=([^;]*)`),
  );

  return parseDatadogSampleRatesCookieValue(match?.[1]);
};

/**
 * Set on proxy responses as comma-separated rates (e.g. `100,50`).
 */
export const applyDatadogSessionSampleRateCookie = (response, rates) => {
  if (!response?.cookies) {
    return response;
  }

  const resolved = resolveDatadogSessionSampleRates(rates);

  response.cookies.set(
    DATADOG_SESSION_SAMPLE_RATE_COOKIE,
    formatDatadogSampleRatesCookieValue(resolved),
    {
      path: "/",
      sameSite: "lax",
      maxAge: COOKIE_MAX_AGE_SECONDS,
    },
  );

  return response;
};

/**
 * Browser RUM init: proxy cookie → env → defaults.
 */
export const getResolvedDatadogSessionSampleRatesForBrowser = () =>
  resolveDatadogSessionSampleRates(readDatadogSessionSampleRateCookie());
